import React, { PropsWithChildren, useRef, useMemo, useCallback, ReactNode } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { mergeRight } from 'ramda'
import Text from '../Text'
import Theme from 'core/themes/model'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import useToggler from 'core/hooks/useToggler'
import { AlignHorizontal, AlignVertical } from 'core/components/codeMirror/CodeMirror'
import HintTooltip from 'core/elements/tooltip/HintTooltip'

export interface InputIconProps {
  onClick?: () => void
  placement?: 'start' | 'end'
}

interface Align {
  vertical: AlignVertical
  horizontal: AlignHorizontal
}

interface Offset {
  vertical: number
  horizontal: number
}

export interface InputProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  className?: string
  compact?: boolean
  variant?: 'light' | 'dark'
  mask?: string
  iconProps?: InputIconProps
  icon?: string
  label?: string
  info?: string | ReactNode
  tooltipClassName?: string
  disabled?: boolean
  error?: string
  onChange?: (e: any) => void
  onBlur?: (e: any) => void
  align?: Align
  offset?: Offset
  origin?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
}

const defaultIconProps: InputIconProps = {
  placement: 'start',
}

const Input = ({
  className = undefined,
  compact = false,
  variant = 'light',
  mask = undefined,
  icon = undefined,
  iconProps = undefined,
  placeholder = '',
  label = '',
  info = '',
  tooltipClassName = undefined,
  children,
  value,
  onChange,
  onBlur,
  disabled = false,
  align,
  offset,
  origin,
  error = '',
  startAdornment,
  endAdornment,
  ...rest
}: PropsWithChildren<InputProps>) => {
  const maskRef = useRef<HTMLParagraphElement>(undefined)
  const width = useMemo(() => {
    return maskRef?.current?.getBoundingClientRect()?.width || 0
  }, [maskRef.current, mask])

  const [focused, toggleFocused] = useToggler(false)
  const allIconProps = useMemo(() => mergeRight(defaultIconProps, iconProps || {}), [iconProps])
  const handleChange = useCallback(
    (event) => {
      if (onChange) {
        onChange(event)
      }
    },
    [onChange],
  )

  const handleFocusOut = (event) => {
    event.target.value = event?.target?.value?.trim()
    onChange(event)
    toggleFocused()
  }

  const handleFocusToggle = useCallback(() => {
    toggleFocused()
  }, [toggleFocused])
  const classes = useStyles()
  let extraContent = null
  if (mask && !!value) {
    extraContent = (
      <Text
        variant="inputPlaceholder"
        ref={maskRef}
        className={clsx(classes.maskText, { [classes.maskTextWithIcon]: !!icon })}
      >
        {mask}
      </Text>
    )
  }
  return (
    <div
      className={clsx(classes.wrapper, className, {
        [classes.wrapperWithInfo]: !!info,
        [classes.wrapperCompact]: compact,
      })}
      style={{
        gridTemplateColumns: !!info ? 'max-content max-content 1fr' : '1fr',
        gridTemplateAreas: !!info
          ? `"input-label input-hint spacer" "input-frame input-frame input-frame"${
              error ? ' "input-error input-error input-error"' : ''
            }`
          : `"input-label input-label" "input-frame input-frame"${
              error ? ' "input-error input-error"' : ''
            }`,
      }}
    >
      {label && (
        <Text
          variant="inputLabel"
          className={clsx(classes.label, 'label', { [classes.labelDisabled]: disabled })}
        >
          {label}
        </Text>
      )}
      {info && (
        <HintTooltip
          message={info || label || placeholder}
          align={align}
          offset={offset}
          origin={origin}
          customClassName={tooltipClassName}
          className={classes.info}
        />
      )}

      <div
        className={clsx(classes.inputFrame, 'inputFrame', {
          [classes.inputFrameCompact]: compact,
          [classes.inputFrameDisabled]: disabled,
          [classes.inputFrameError]: !!error,
          [classes.inputFrameFocused]: focused,
        })}
        style={{
          gridTemplateColumns: (() => {
            const start = icon && allIconProps.placement !== 'end' ? 'max-content ' : ''
            const startAd = !!startAdornment ? 'max-content ' : ''
            const end = icon && allIconProps.placement === 'end' ? ' max-content' : ''
            const endAd = !!endAdornment ? ' max-content' : ''
            return `${startAd}${start}1fr${end}${endAd}`.trim()
          })(),
          padding: (() => {
            const leftPaddDefault = 8
            const leftPadd = width > 0 ? width + leftPaddDefault : leftPaddDefault
            return `0 ${!!endAdornment ? 0 : 8}px 0 ${!!startAdornment ? 0 : leftPadd}px`
          })(),
        }}
      >
        {/* Render icon at start if placement is not 'end' */}
        {icon && allIconProps.placement !== 'end' && (
          <FontAwesomeIcon className={clsx(classes.icon, 'icon')} onClick={allIconProps.onClick}>
            {icon}
          </FontAwesomeIcon>
        )}
        {extraContent}
        {startAdornment && (
          <div className={clsx(classes.adornment, classes.startAdornment)}>{startAdornment}</div>
        )}
        <input
          data-testid="input--input-0"
          {...rest}
          onFocus={handleFocusToggle}
          onBlur={onBlur ? onBlur : handleFocusOut}
          value={value}
          onChange={handleChange}
          placeholder={placeholder || label}
          className={clsx(classes.input, 'input', {
            'input-disabled': disabled,
            [classes.inputDisabled]: disabled,
          })}
        >
          {children}
        </input>
        {/* Render icon at end if placement is 'end', before endAdornment */}
        {icon && allIconProps.placement === 'end' && (
          <FontAwesomeIcon className={clsx(classes.icon, 'icon')} onClick={allIconProps.onClick}>
            {icon}
          </FontAwesomeIcon>
        )}
        {endAdornment && (
          <div className={clsx(classes.adornment, classes.endAdornment)}>{endAdornment}</div>
        )}
      </div>
      {!!error && (
        <Text variant="body2" className={classes.error}>
          {error}
        </Text>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  wrapper: {
    display: 'grid',
    maxWidth: 400,
    minWidth: 100,
    width: '100%',
    gridAutoRows: 'max-content',
    gap: 8,
  },
  wrapperCompact: {
    gap: 0,
  },
  wrapperWithInfo: {
    // gridTemplateColumns and gridTemplateAreas set via inline style
  },
  error: {
    marginLeft: 4,
    gridArea: 'input-error',
    color: theme.components.input.error,
    lineHeight: '16px',
  },
  label: {
    marginLeft: 4,
    gridArea: 'input-label',
    transition: 'color .2s ease',
    color: theme.components.input.label.color,
  },
  labelDisabled: {
    color: theme.components.input.label.disabled,
  },
  info: {
    gridArea: 'input-hint',
    display: 'grid',
    cursor: 'help',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'help',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
      '&:hover': { color: theme.palette.grey[700] },
    },
  },
  inputFrame: {
    gridArea: 'input-frame',
    position: 'relative',
    borderWidth: 1,
    borderStyle: 'solid',
    cursor: 'pointer',
    pointerEvents: 'unset',
    borderColor: theme.components.input.frame.border,
    backgroundColor: theme.components.input.frame.background,
    transition: 'border-color .2s ease',
    borderRadius: 4,
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 8,
  },
  inputFrameCompact: {
    borderWidth: 0,
  },
  inputFrameDisabled: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    backgroundColor: theme.components.input.frame.disabledBackground,
  },
  inputFrameError: {
    borderColor: theme.components.input.error,
  },
  inputFrameFocused: {
    borderColor: theme.components.input.frame.activeBorder,
  },
  maskText: {
    position: 'absolute',
    top: '50%',
    left: 8,
    zIndex: 1,
    transform: 'translate(0, -50%)',
    color: theme.components.input.frame.color,
    transition: 'color .2s ease',
  },
  maskTextWithIcon: {
    left: 32,
  },
  icon: {
    fontSize: 16,
    lineHeight: '16px',
    padding: 4,
    borderRadius: 4,
    color: theme.components.input.frame.color,
    transition: 'color .2s ease',
  },
  iconCompact: {
    fontSize: 12,
  },
  iconDisabled: {
    color: theme.components.input.frame.disabledPlaceholder,
  },
  input: {
    ...theme.typography.inputPlaceholder,
    transition: 'color .2s ease',
    minHeight: 36,
    outline: 0,
    padding: 0,
    margin: 0,
    width: '100%',
    color: theme.components.input.frame.color,
    caretColor: theme.components.input.frame.color,
    background: 'transparent',
    boxSizing: 'border-box',
    border: 'none',
  },
  inputDisabled: {
    color: theme.components.input.frame.disabledPlaceholder,
  },
  adornment: {
    color: theme.components.input.frame.color,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
  startAdornment: {
    marginRight: theme.spacing(0.5),
  },
  endAdornment: {
    marginLeft: theme.spacing(0.5),
  },
  '@global': {
    // unfortunately globals wont combine
    'input::placeholder': {
      transition: 'color .2s ease',
      color: theme.components.input.frame.placeholder,
    },
    'input::-webkit-input-placeholder': {
      transition: 'color .2s ease',
      color: theme.components.input.frame.placeholder,
    },
    'input::-moz-placeholder': {
      transition: 'color .2s ease',
      color: theme.components.input.frame.placeholder,
    },
    'input:-ms-input-placeholder': {
      transition: 'color .2s ease',
      color: theme.components.input.frame.placeholder,
    },
    'input:-moz-placeholder': {
      transition: 'color .2s ease',
      color: theme.components.input.frame.placeholder,
    },

    'input.input-disabled::placeholder': {
      color: [theme.components.input.frame.disabledPlaceholder, '!important'],
    },
    'input.input-disabled::-webkit-input-placeholder': {
      color: [theme.components.input.frame.disabledPlaceholder, '!important'],
    },
    'input.input-disabled::-moz-placeholder': {
      color: [theme.components.input.frame.disabledPlaceholder, '!important'],
    },
    'input.input-disabled:-ms-input-placeholder': {
      color: [theme.components.input.frame.disabledPlaceholder, '!important'],
    },
    'input.input-disabled:-moz-placeholder': {
      color: [theme.components.input.frame.disabledPlaceholder, '!important'],
    },

    '*:-webkit-autofill': {
      transition: 'background-color 10000s', // dont let the background change
      transitionDelay: '10000s',
      '-webkit-text-fill-color': theme.components.input.frame.color,
      '-webkit-box-shadow': `0 0 0 30px ${theme.components.input.frame.background} inset !important`,
    },
    '*:-webkit-autofill:hover': {
      transition: 'background-color 10000s', // dont let the background change
      transitionDelay: '10000s',
      '-webkit-text-fill-color': theme.components.input.frame.color,
      '-webkit-box-shadow': `0 0 0 30px ${theme.components.input.frame.background} inset !important`,
    },
    '*:-webkit-autofill:focus': {
      transition: 'background-color 10000s', // dont let the background change
      transitionDelay: '10000s',
      '-webkit-text-fill-color': theme.components.input.frame.color,
      '-webkit-box-shadow': `0 0 0 30px ${theme.components.input.frame.background} inset !important`,
    },
    '*:-webkit-autofill:active': {
      transition: 'background-color 10000s', // dont let the background change
      transitionDelay: '10000s',
      '-webkit-text-fill-color': theme.components.input.frame.color,
      '-webkit-box-shadow': `0 0 0 30px ${theme.components.input.frame.background} inset !important`,
    },
  },
}))

export default Input
